/**
 * Person Card accordion modal — replaces the legacy 4-tab EditMemberDialog.
 *
 * Layout (one section expanded at a time, collapsed sections show summary):
 *   1. Roles                — checkboxes + co-sign / prescribing-proxy sub-blocks
 *   2. Tier & Add-ons       — default expanded; role-gated to Clinician
 *   3. Profile              — autosaves per field via useAutoSave
 *   4. Billing & Claims     — autosaves per field via useAutoSave
 *
 * Footer:
 *   - Cancel
 *   - "Confirm — $X/mo" when plan changes pending
 *   - "Save" when only role/membership changes pending
 *   - disabled when nothing's pending (profile/billing autosave handles theirs)
 */

import { useCallback, useEffect, useState } from 'react';
import { ConfirmDialog } from '@components/primitives/ConfirmDialog';
import { formatPrice } from '@features/subscriptions';
import { ProviderBadge, ScrollFade } from '@jotpsych/ui';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Skeleton,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';
import type { ProviderRoleInfo } from '@store/generated/organizations';
import { getRoleDisplayName, ROLES } from '@utils/roleUtils';

import type { OrganizationMemberTableRow } from '../../types';

import { BillingSection } from './BillingSection';
import { ProfileSection } from './ProfileSection';
import { RolesSection } from './RolesSection';
import { TierAddOnsSection } from './TierAddOnsSection';
import { usePersonCard } from './usePersonCard';

type AccordionKey = 'roles' | 'tier' | 'profile' | 'billing';

// Default fallback role list when the parent doesn't pass availableRoles in
// (mirrors the legacy EditMemberDialog defaults).
const DEFAULT_ROLES: ProviderRoleInfo[] = [
  { role: ROLES.CLINICIAN, display_name: 'Clinician', description: 'Clinical staff member' },
  {
    role: ROLES.OPERATIONS_SUPPORT,
    display_name: 'Operations Support',
    description: 'Operations team member',
  },
  {
    role: ROLES.PRACTICE_ADMINISTRATOR,
    display_name: 'Practice Administrator',
    description: 'Practice admin with billing access',
  },
  {
    role: ROLES.BILLING_ADMINISTRATOR,
    display_name: 'Billing Administrator',
    description: 'Billing-only access',
  },
  { role: ROLES.SUPERVISOR, display_name: 'Supervisor', description: 'Clinical supervisor' },
];

const accordionSx = {
  boxShadow: 'none',
  '&:before': { display: 'none' },
  '&.Mui-expanded': { margin: 0 },
};

// `px: 3` aligns the summary's left/right edge with DialogTitle and
// DialogActions (24px). MUI's AccordionSummary default is 16px which leaves a
// visible inset against the modal's header/footer.
const summarySx = {
  minHeight: 56,
  px: 3,
  '&.Mui-expanded': { minHeight: 56 },
  '& .MuiAccordionSummary-content': { my: 1.25 },
  '& .MuiAccordionSummary-content.Mui-expanded': { my: 1.25 },
};

interface PersonCardModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  member: OrganizationMemberTableRow | null;
  availableRoles?: ProviderRoleInfo[];
}

export function PersonCardModal({
  open,
  onClose,
  onSuccess,
  member,
  availableRoles = [],
}: PersonCardModalProps) {
  const { practice } = useEventTracking();
  const card = usePersonCard({ member, open, onSuccess });

  // `false` = all sections collapsed; matches the prototype's collapse-all
  // affordance so a user can click the open section's header to close it.
  const [expanded, setExpanded] = useState<AccordionKey | false>('tier');
  const [confirmDeactivateOpen, setConfirmDeactivateOpen] = useState(false);

  // Reset to default-expanded section + fire opened event when the modal opens.
  useEffect(() => {
    if (open && member) {
      setExpanded('tier');
      practice.personCardOpened({
        member_id: member.userId,
        role_count: member.roles.length,
        has_tier: !!member.bundles?.find(b => b.bundle_type === 'tier' && b.status === 'active'),
        addon_count:
          member.bundles?.filter(b => b.bundle_type === 'add_on' && b.status === 'active').length ||
          0,
      });
    }
  }, [open, member, practice]);

  const handleAccordionChange = useCallback(
    (section: AccordionKey) => (_: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? section : false);
      // Only emit the analytic on expand — collapse-to-none isn't a section
      // engagement signal we care to track separately.
      if (isExpanded && member) {
        practice.personCardSectionExpanded({
          member_id: member.userId,
          section,
        });
      }
    },
    [member, practice],
  );

  const handleClose = useCallback(() => {
    if (card.isSaving) return;
    onClose();
  }, [card.isSaving, onClose]);

  const handleCommit = useCallback(() => {
    card.commitChanges();
  }, [card]);

  if (!member) return null;

  const rolesToDisplay = availableRoles.length > 0 ? availableRoles : DEFAULT_ROLES;

  // Footer label / disabled logic
  const hasFooterChanges =
    card.hasPlanChanges || card.hasRolesChanges || card.hasMembershipChanges;
  const projectedPriceStr = formatPrice(card.projectedCost.cents, card.projectedCost.currency);
  const footerLabel = card.hasPlanChanges
    ? card.isTrialing
      ? `Confirm — ${projectedPriceStr}/mo after trial`
      : `Confirm — ${projectedPriceStr}/mo`
    : 'Save';
  const footerDisabled =
    card.isSaving ||
    !hasFooterChanges ||
    (card.canSelectBundles && card.isClinician && !card.selectedTierId);

  // Collapsed-state summary helpers (mirrors prototype)
  const rolesSummary =
    Array.from(card.selectedRoles).length > 0 ? (
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
        {Array.from(card.selectedRoles).map(r => (
          <Chip
            key={r}
            label={getRoleDisplayName(r)}
            size="small"
            variant="outlined"
            sx={{ height: 20, fontSize: '0.65rem' }}
          />
        ))}
      </Box>
    ) : (
      <Typography variant="caption" color="text.secondary">
        No roles
      </Typography>
    );

  const tierSummary = (() => {
    if (!card.isClinician) return '$0/mo — non-clinician roles are free';
    if (!card.selectedTierId) return 'No tier selected';
    const tier = card.tiers.find(t => t.bundle_id === card.selectedTierId);
    if (!tier) return '';
    const cost = formatPrice(card.projectedCost.cents, card.projectedCost.currency);
    const addOnNames = Array.from(card.selectedAddOnIds)
      .map(id => card.availableAddOns.find(a => a.bundle_id === id)?.name)
      .filter(Boolean)
      .join(', ');
    return addOnNames ? `${tier.name} — ${cost}/mo · ${addOnNames}` : `${tier.name} — ${cost}/mo`;
  })();

  const profileSummary = `${card.profileData.first_name || ''} ${
    card.profileData.last_name || ''
  }`.trim() || card.profileData.email || member.email || '—';

  const billingSummary = (() => {
    const parts: string[] = [];
    if (card.billingData.npi_number) parts.push(`NPI ${card.billingData.npi_number}`);
    if (card.billingData.license_type) parts.push(card.billingData.license_type);
    if (card.billingData.taxonomy_code) parts.push(`Taxonomy ${card.billingData.taxonomy_code}`);
    return parts.length > 0 ? parts.join(' · ') : 'NPI, taxonomy, license, facility info';
  })();

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <ProviderBadge name={member.name} size="regular" />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {member.name || 'Unknown'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {member.email || 'No email'}
              </Typography>
            </Box>
          </Box>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            disabled={card.isSaving}
            size="small"
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{ p: 0, display: 'flex', flexDirection: 'column', maxHeight: '70vh' }}
      >
        <ScrollFade>
          {card.isLoading ? (
            <Box sx={{ p: 3 }}>
              <Skeleton variant="rounded" sx={{ height: 320, mb: 2 }} />
              <Skeleton variant="rounded" sx={{ height: 80 }} />
            </Box>
          ) : (
            <>
              {/* Roles */}
              <Accordion
                expanded={expanded === 'roles'}
                onChange={handleAccordionChange('roles')}
                disableGutters
                square
                sx={accordionSx}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2">Roles</Typography>
                    {expanded !== 'roles' && rolesSummary}
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0, pb: 3, px: 3 }}>
                  <RolesSection
                    rolesToDisplay={rolesToDisplay}
                    selectedRoles={card.selectedRoles}
                    onToggleRole={card.toggleRole}
                    needsCosign={card.needsCosign}
                    onNeedsCosignChange={card.setNeedsCosign}
                    canPrepareOnBehalf={card.canPrepareOnBehalf}
                    onCanPrepareOnBehalfChange={card.setCanPrepareOnBehalf}
                    disabled={card.isSaving}
                  />
                </AccordionDetails>
              </Accordion>

              <Divider />

              {/* Tier & Add-ons */}
              <Accordion
                expanded={expanded === 'tier'}
                onChange={handleAccordionChange('tier')}
                disableGutters
                square
                sx={accordionSx}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2">Tier & Add-ons</Typography>
                    {expanded !== 'tier' && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 0.25 }}
                      >
                        {tierSummary}
                      </Typography>
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0, pb: 3, px: 3 }}>
                  <TierAddOnsSection
                    isClinician={card.isClinician}
                    canSelectBundles={card.canSelectBundles}
                    isCatalogLoading={card.isCatalogLoading}
                    tiers={card.tiers}
                    availableAddOns={card.availableAddOns}
                    selectedTierId={card.selectedTierId}
                    selectedAddOnIds={card.selectedAddOnIds}
                    currentTierBundleId={card.currentTierBundleId}
                    onTierChange={card.changeTier}
                    onToggleAddOn={card.toggleAddOn}
                    isTrialing={card.isTrialing}
                    hasPlanChanges={card.hasPlanChanges}
                    currentCost={card.currentCost}
                    projectedCost={card.projectedCost}
                    billingInterval="month"
                  />
                </AccordionDetails>
              </Accordion>

              <Divider />

              {/* Profile (autosave) */}
              <Accordion
                expanded={expanded === 'profile'}
                onChange={handleAccordionChange('profile')}
                disableGutters
                square
                sx={accordionSx}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2">Profile</Typography>
                    {expanded !== 'profile' && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 0.25 }}
                      >
                        {profileSummary}
                      </Typography>
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0, pb: 3, px: 3 }}>
                  <ProfileSection
                    initialData={card.profileData}
                    onSave={card.persistProfile}
                    disabled={card.isSaving}
                  />
                </AccordionDetails>
              </Accordion>

              <Divider />

              {/* Billing & Claims (autosave) */}
              <Accordion
                expanded={expanded === 'billing'}
                onChange={handleAccordionChange('billing')}
                disableGutters
                square
                sx={accordionSx}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2">Billing & Claims</Typography>
                    {expanded !== 'billing' && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 0.25 }}
                      >
                        {billingSummary}
                      </Typography>
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0, pb: 3, px: 3 }}>
                  <BillingSection
                    initialData={card.billingData}
                    onSave={card.persistBilling}
                    disabled={card.isSaving}
                    billingEntities={card.billingEntities}
                    entitiesLoading={card.entitiesLoading}
                    serviceFacilities={card.serviceFacilities}
                    facilitiesLoading={card.facilitiesLoading}
                  />
                </AccordionDetails>
              </Accordion>
            </>
          )}
        </ScrollFade>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
        {!card.isEditingSelf ? (
          <Button
            variant="outlined"
            color="error"
            onClick={() => setConfirmDeactivateOpen(true)}
            disabled={card.isSaving || card.isDeactivating}
          >
            Deactivate Member
          </Button>
        ) : (
          <Box />
        )}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={handleClose} disabled={card.isSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleCommit}
            color="primary"
            variant="contained"
            disabled={footerDisabled}
            startIcon={card.isSaving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {card.isSaving ? 'Saving…' : footerLabel}
          </Button>
        </Box>
      </DialogActions>

      <ConfirmDialog
        open={confirmDeactivateOpen}
        variant="error"
        title="Deactivate Member?"
        message={`Are you sure you want to deactivate ${
          member.name || 'this member'
        }? They will lose access to the organization and their subscription will be cancelled.`}
        onClose={() => setConfirmDeactivateOpen(false)}
        onConfirm={card.deactivate}
        cancelText="Cancel"
        confirmText="Deactivate"
        loading={card.isDeactivating}
      />
    </Dialog>
  );
}
